import { useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import Actionbar from "src/components/common/actionbar/Authactionbar";
import { Message } from "src/components/common/form/Message";
import useUiuxStore from "src/store/useUiuxStore";
import { useMouseContent } from "src/utils/mouse/mouseContent";
import { HRM3050_R01Content } from "./Content_HRM3050_R01";
import { DetailTable_HRM3050_R01 } from "./DetailTable_HRM3050_R01";
import { HRM3050_R01DetailDto, HRM3050_R01HeaderDto } from "./Dto_HRM3050_R01";
import { HRM3050_R01SearchSection } from "./SearchSection_HRM3050_R01";
import { useHRM3050_R01Store } from "./Store_HRM3050_R01";
import { Summary_HRM3050_R01 } from "./Summary_HRM3050_R01";
import { Table_HRM3050_R01 } from "./Table_HRM3050_R01";

// 위아래 분할 리사이저 설정. 위쪽(근무조) 그리드 높이 비율(%)을 브라우저에 저장한다. 실행 샘플과 같이 기본 30%로 둔다.
const RESIZER = {
    code: 'HRM3050_R01',
    type: 'height',
    storageKey: 'HRM3050_R01-split-height',
    min: 5,
    max: 80,
    default: 30,
} as const;

/**
 * 화면의 액션바, 검색 영역(일일근태 재계산 버튼 포함), 위쪽 근무조 그리드, 아래쪽 시간 요약·휴게 그리드, 메시지를 조립한다.
 * 등록·삭제 버튼은 마지막으로 클릭한 그리드에 적용된다. 근무조는 삭제할 수 없다.
 * 자동 조회는 하지 않으며, 빈 목록에 입력용 빈 행을 넣는 처리는 Content가 담당한다.
 * @returns HRM3050_R01 화면 JSX
 */
export const Container_HRM3050_R01 = () => {
    // 근무조 그리드 인스턴스를 가리키는 ref. 편집 확정과 스크롤 이동에 쓴다.
    const gridRef = useRef<AgGridReact<HRM3050_R01HeaderDto>>(null);
    // 휴게 그리드 인스턴스를 가리키는 ref. 편집 확정에 쓴다.
    const detailGridRef = useRef<AgGridReact<HRM3050_R01DetailDto>>(null);
    // 위아래 분할 영역 전체를 가리키는 ref. 리사이저가 마우스 위치를 높이 비율로 바꿀 때 기준으로 쓴다.
    const containerRef = useRef<HTMLDivElement>(null);
    // 아래쪽(휴게) 영역을 가리키는 ref. 공통 리사이저 Hook에 전달한다.
    const infoRef = useRef<HTMLDivElement>(null);
    // true이면 리사이저를 드래그하는 중이다. 공통 리사이저 Hook이 값을 바꾼다.
    const isResizing = useRef(false);
    const { setMessage } = useUiuxStore();
    // 화면 Store에서 등록·삭제 대상 그리드 변경 함수를 가져온다.
    const { setActiveGridType } = useHRM3050_R01Store();
    // 공통 리사이저 Hook에서 위쪽 그리드 높이 비율과 드래그 시작 함수를 가져온다. (MST4060_R01 상세 영역과 같은 배치)
    const { splitValue, handleMouseDown } = useMouseContent({
        resizer: RESIZER,
        isResizing,
        containerRef,
        infoRef,
    });
    const {
        clickable, setClickable, fetchData,
        defaultHRM3050_R01HeaderRow, defaultHRM3050_R01DetailRow, initContainerData,
        handleHeaderRowClick, handleInsertClick, handleSaveClick, handleDeleteClick,
        handleExcelClick, handleRecalculateClick,
    } = HRM3050_R01Content({ gridRef, detailGridRef, setMessage });

    // Content가 제공한 버튼 동작과 그리드 참조를 각 화면 요소에 연결한다.
    return (
        <div className="content-container">
            {/* 공통 액션바: 조회·등록·삭제·저장·Excel·초기화를 Content 함수에 연결한다. 출력은 연결하지 않아 비활성이다. */}
            <Actionbar
                onFetchClick={fetchData}
                onCreateClick={handleInsertClick}
                onDeleteClick={handleDeleteClick}
                onSaveClick={handleSaveClick}
                onExcelClick={handleExcelClick}
                onInitClick={initContainerData}
            />
            {/* 검색 영역: 사용여부 콤보와 화면 전용 "일일근태 재계산" 버튼 */}
            <HRM3050_R01SearchSection onRecalculateClick={() => { void handleRecalculateClick(); }} />
            {/* 위아래 분할 영역 */}
            <div className="flex flex-col flex-1 p-[12px] gap-[4px] min-h-0" ref={containerRef}>
                {/* 위쪽 근무조 영역: 클릭하면 등록·삭제 대상 그리드가 된다. 셀 클릭 등이 이벤트를 멈춰도 먼저 지정되도록 캡처 단계에서 처리한다.
                    mousedown이 아니라 click에서 바꾼다. mousedown에서 Store를 바꾸면 화면이 다시 그려지면서 그리드 컬럼 정의가 새로 만들어지고,
                    행 번호 칸의 + 버튼이 mouseup 전에 다시 그려져 click 이벤트가 사라진다. click은 버튼 동작이 끝난 뒤 다시 그려지므로 영향이 없다. */}
                <div
                    className="w-full min-h-0"
                    style={{ height: `${splitValue}%` }}
                    onClickCapture={() => setActiveGridType('header')}
                >
                    {/* 근무조 그리드: 행 클릭으로 선택 근무조를 바꾸고, 선택 행만 편집한다. */}
                    <Table_HRM3050_R01
                        gridRef={gridRef}
                        defaultHRM3050_R01HeaderRow={defaultHRM3050_R01HeaderRow()}
                        handleHeaderRowClick={handleHeaderRowClick}
                    />
                </div>
                {/* 리사이저: 드래그하면 위아래 영역 높이 비율을 바꾼다. */}
                <div
                    className="h-[6px] bg-gray-100 cursor-row-resize hover:bg-gray-300"
                    onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => handleMouseDown('UD', e.nativeEvent)}
                />
                {/* 아래쪽 휴게 영역: 제목·요약·그리드가 간격 없이 이어지며, 클릭하면 등록·삭제 대상 그리드가 된다. */}
                <div
                    className="w-full min-h-0 flex flex-col"
                    style={{ height: `${100 - splitValue}%` }}
                    ref={infoRef}
                    onClickCapture={() => setActiveGridType('detail')}
                >
                    {/* 선택 근무조 제목과 정시 기준 시간 요약 */}
                    <Summary_HRM3050_R01 />
                    <div className="flex-1 min-h-0">
                        {/* 휴게 그리드: 선택 근무조의 휴게를 편집하고 하단에 건수·시간(분) 합계를 표시한다. */}
                        <DetailTable_HRM3050_R01
                            clickable={clickable}
                            setClickable={setClickable}
                            gridRef={detailGridRef}
                            defaultHRM3050_R01DetailRow={defaultHRM3050_R01DetailRow()}
                            handleInsertClick={handleInsertClick}
                        />
                    </div>
                </div>
            </div>
            <Message />
        </div>
    );
};
